import React, { useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import RunnerAvatar, { RunnerState } from './RunnerAvatar';
import { calculateEta } from '../../hooks/useEta';
import type { MemberLocationMap } from '../../hooks/useRealtimeMemberLocations';
import { IcRun, IcMedal, IcWarning, IcCafe, IcConstruction, IcClock } from 'components/common/icons/EmojiIcons';
import styles from './RaceBottomSheet.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface RaceParticipant {
    userNo: number;
    nickname: string;
    profileImgUrl?: string;
    attendanceStatus?: string; // ATTENDING 만 레이스 포함
}

export interface RaceBottomSheetProps {
    participants: RaceParticipant[];
    memberLocations: MemberLocationMap;
    destination: { latitude: number; longitude: number } | null;
    meetingStartMs: number | null;
    currentUserNo: number | null;
    lateFeeAmount?: number;           // 벌금 (원)
    shuttlePenaltyEnabled?: boolean;  // 꼴등 셔틀 ON/OFF
    isHost?: boolean;                 // 주최자만 설정 변경
    onToggleShuttle?: (enabled: boolean) => void;
    onReportExcuse?: (reason: string) => void;
    onClose?: () => void;
    defaultExpanded?: boolean;
    /** 하단 참여자 시트 높이만큼 위로 띄움. 기본 12. */
    bottomOffset?: number;
}

interface RankedRow {
    userNo: number;
    nickname: string;
    profileImgUrl?: string;
    distance: number;         // meters
    etaMinutes: number | null;
    arrived: boolean;
    arrivedOrderIndex?: number; // 도착 순서
    state: RunnerState;
    projectedLateMinutes: number;
    hasLocation: boolean;
    rank: number;
    isLast: boolean;
    isMe: boolean;
}
// race-row 에서 렌더 시 RunnerAvatar 에 profileImgUrl 그대로 전달

function formatDistance(meters: number): string {
    if (meters >= 1000) return `${(meters / 1000).toFixed(1)}km`;
    return `${Math.round(meters)}m`;
}

function formatEta(eta: EtaPretty): string {
    if (eta.arrived) return i18n.t('race.arriveDone');
    if (eta.etaMinutes == null) return i18n.t('race.waiting');
    if (eta.etaMinutes <= 0) return i18n.t('race.soonArrive');
    return i18n.t('race.minLeft', { n: eta.etaMinutes });
}

type EtaPretty = {
    arrived: boolean;
    etaMinutes: number | null;
};

const RaceBottomSheet: React.FC<RaceBottomSheetProps> = ({
    participants,
    memberLocations,
    destination,
    meetingStartMs,
    currentUserNo,
    lateFeeAmount = 0,
    shuttlePenaltyEnabled = true,
    isHost = false,
    onToggleShuttle,
    onReportExcuse,
    onClose,
    defaultExpanded = false,
    bottomOffset = 12,
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [showExcuse, setShowExcuse] = useState(false);
    const [excuseReason, setExcuseReason] = useState('');
    // ETA/남은 시간 실시간 갱신용 tick (15초)
    const [nowTick, setNowTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setNowTick(v => v + 1), 15000);
        return () => clearInterval(id);
    }, []);

    const ranked: RankedRow[] = useMemo(() => {
        if (!destination || !meetingStartMs) return [];

        const rows: Omit<RankedRow, 'rank' | 'isLast'>[] = participants
            .filter(p => p.attendanceStatus === 'ATTENDING')
            .map(p => {
                const loc = memberLocations[p.userNo];
                if (!loc) {
                    return {
                        userNo: p.userNo,
                        nickname: p.nickname,
                        profileImgUrl: p.profileImgUrl,
                        distance: Infinity,
                        etaMinutes: null,
                        arrived: false,
                        state: 'stopped' as RunnerState,
                        projectedLateMinutes: 0,
                        hasLocation: false,
                        isMe: p.userNo === currentUserNo,
                    };
                }
                const eta = calculateEta({
                    history: loc.history,
                    destination,
                    meetingStartMs,
                });
                return {
                    userNo: p.userNo,
                    nickname: p.nickname,
                    profileImgUrl: p.profileImgUrl,
                    distance: eta.distanceMeters,
                    etaMinutes: eta.etaMinutes,
                    arrived: eta.arrived,
                    state: eta.state,
                    projectedLateMinutes: eta.projectedLateMinutes,
                    hasLocation: true,
                    isMe: p.userNo === currentUserNo,
                };
            });

        // 정렬: 도착자(거리 오름차순) → 미도착자(ETA 오름차순, ETA null 은 거리 기준 맨뒤)
        rows.sort((a, b) => {
            if (a.arrived && !b.arrived) return -1;
            if (!a.arrived && b.arrived) return 1;
            if (a.arrived && b.arrived) return a.distance - b.distance;
            // 둘 다 미도착
            const aEta = a.etaMinutes ?? 9999;
            const bEta = b.etaMinutes ?? 9999;
            if (aEta !== bEta) return aEta - bEta;
            return a.distance - b.distance;
        });

        const realLastIdx = rows.length > 0 && !rows[rows.length - 1].arrived
            ? rows.length - 1
            : -1;

        return rows.map((row, i) => ({
            ...row,
            rank: i + 1,
            isLast: i === realLastIdx && rows.length > 1 && !row.arrived,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participants, memberLocations, destination, meetingStartMs, currentUserNo, nowTick]);

    // 내 순위 + 약속까지 남은 시간
    const myRow = ranked.find(r => r.isMe);
    const minutesUntilStart = meetingStartMs
        ? Math.round((meetingStartMs - Date.now()) / 60000)
        : null;

    const compactTime = (() => {
        if (minutesUntilStart == null) return '';
        if (minutesUntilStart > 0) return t('race.minLeft', { n: minutesUntilStart });
        if (minutesUntilStart === 0) return t('race.startingNow');
        return t('race.minPast', { n: Math.abs(minutesUntilStart) });
    })();

    return (
        <div
            className={cls(`race-sheet ${expanded ? 'expanded' : 'collapsed'}`)}
            style={{ ['--race-sheet-bottom' as any]: `${bottomOffset}px` }}
        >
            {/* 접힘 바 */}
            <button
                className={cls("race-sheet-handle")}
                onClick={() => setExpanded(v => !v)}
                aria-expanded={expanded}
            >
                <svg className={cls("race-sheet-icon")} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M4 21V4" />
                    <path d="M4 4h12l-3 4 3 4H4" />
                </svg>
                <span className={cls("race-sheet-title")}>{t('race.title')}</span>
                {myRow && (
                    <span className={cls("race-sheet-mine")}>{t('race.rankNth', { rank: myRow.rank })}</span>
                )}
                <span className={cls(`race-sheet-chevron ${expanded ? 'open' : ''}`)} aria-hidden>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </span>
            </button>

            {/* 펼침 본문 */}
            {expanded && (
                <div className={cls("race-sheet-body")}>
                    {ranked.length === 0 ? (
                        <div className={cls("race-empty")}>
                            {t('race.noSharingYet')}<br />
                            <small>{t('race.shareLocFirst')} <IcRun size={13} /></small>
                        </div>
                    ) : (
                        <ul className={cls("race-list")}>
                            {ranked.map(row => {
                                const etaText = formatEta({ arrived: row.arrived, etaMinutes: row.etaMinutes });
                                const distText = row.hasLocation ? formatDistance(row.distance) : t('race.locHidden');
                                const lateWarning = !row.arrived && row.projectedLateMinutes >= 5;
                                return (
                                    <li
                                        key={row.userNo}
                                        className={cls(`race-row ${row.isMe ? 'me' : ''} ${row.isLast ? 'is-last' : ''}`)}
                                    >
                                        <div className={cls("race-rank-badge")}>
                                            {row.rank <= 3 ? <IcMedal size={18} /> : row.rank}
                                        </div>
                                        <RunnerAvatar
                                            state={row.state}
                                            rank={row.rank <= 3 ? row.rank : undefined}
                                            isLast={row.isLast}
                                            nickname={row.nickname}
                                            profileImgUrl={row.profileImgUrl}
                                            size={36}
                                        />
                                        <div className={cls("race-info")}>
                                            <div className={cls("race-nickname")}>
                                                {row.nickname}
                                                {row.isMe && <span className={cls("race-me-pill")}>{t('race.me')}</span>}
                                            </div>
                                            <div className={cls("race-meta")}>
                                                <span className={cls(`race-eta ${lateWarning ? 'late' : ''}`)}>{etaText}</span>
                                                <span className={cls("race-dot")}>·</span>
                                                <span className={cls("race-dist")}>{distText}</span>
                                            </div>
                                        </div>
                                        {lateWarning && lateFeeAmount > 0 && (
                                            <div className={cls("race-fine")}>
                                                <IcWarning size={12} /> {t('race.lateExpected')}<br />
                                                {t('meetingFinished.amountWon', { amount: lateFeeAmount.toLocaleString('ko-KR') })}
                                            </div>
                                        )}
                                        {row.isLast && shuttlePenaltyEnabled && (
                                            <div className={cls("race-shuttle-tag")}><IcCafe size={12} /> {t('race.todayShuttle')}</div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/* 내가 늦는 중이고 벌금이 있는 모임 → 사유 보고 버튼 */}
                    {onReportExcuse && myRow && !myRow.arrived && myRow.projectedLateMinutes >= 5 && lateFeeAmount > 0 && (
                        <div className={cls("race-excuse-block")}>
                            {!showExcuse ? (
                                <button className={cls("race-excuse-btn")} onClick={() => setShowExcuse(true)}>
                                    <IcConstruction size={14} /> {t('race.reportLate')}
                                </button>
                            ) : (
                                <div className={cls("race-excuse-form")}>
                                    <div className={cls("race-excuse-label")}>{t('race.excuseLabel')}</div>
                                    <textarea
                                        value={excuseReason}
                                        onChange={(e) => setExcuseReason(e.target.value)}
                                        maxLength={200}
                                        rows={2}
                                        placeholder={t('race.excusePlaceholder')}
                                    />
                                    <div className={cls("race-excuse-actions")}>
                                        <button className={cls("race-excuse-cancel")} onClick={() => { setShowExcuse(false); setExcuseReason(''); }}>
                                            {t('race.cancelBtn')}
                                        </button>
                                        <button
                                            className={cls("race-excuse-submit")}
                                            onClick={() => {
                                                if (excuseReason.trim()) {
                                                    onReportExcuse(excuseReason.trim());
                                                    setShowExcuse(false);
                                                    setExcuseReason('');
                                                }
                                            }}
                                            disabled={!excuseReason.trim()}
                                        >
                                            {t('race.submitBtn')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className={cls("race-sheet-footer")}>
                        <div className={cls("race-sheet-footer-line")}>
                            {lateFeeAmount > 0 && (
                                <span>
                                    <IcClock size={13} />{' '}
                                    {t('race.feePerLate', {
                                        amount: t('meetingFinished.amountWon', { amount: lateFeeAmount.toLocaleString('ko-KR') }),
                                    })}
                                </span>
                            )}
                        </div>
                        {isHost && onToggleShuttle && (
                            <label className={cls("race-shuttle-toggle")}>
                                <span><IcCafe size={14} /> {t('race.shuttlePenalty')}</span>
                                <input
                                    type="checkbox"
                                    checked={shuttlePenaltyEnabled}
                                    onChange={(e) => onToggleShuttle(e.target.checked)}
                                />
                                <span className={cls("race-toggle-slider")}>
                                    <span className={cls("race-toggle-knob")} />
                                </span>
                            </label>
                        )}
                        {onClose && (
                            <button className={cls("race-sheet-close")} onClick={onClose}>
                                {t('race.hide')}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RaceBottomSheet;
